import { useCallback } from "react";
import { dataActions } from "@src/services/state/dataReducer";
import type { CommonSettingsData } from "@src/types";
import type * as TDT from "@tradingdroid/frontends-types";
import { useDispatch } from "react-redux";

import { useDataState } from "./data";

interface BacktestActions {
  removeBacktest: (backtestId: number) => void;
  upsertBacktest: (backtest: TDT.ApiBacktest) => void;
  setBacktests: (newBacktests: TDT.ApiBacktest[]) => void;
  setBacktestFromExternal: (data: CommonSettingsData & { config: string }) => void;
  clearBacktestFromExternal: () => void;
}

/**
 * Hook for backtest actions only (no data subscription - won't cause re-renders)
 * Use this when you only need to dispatch actions, not read data
 */
export const useBacktestActions = (): BacktestActions => {
  const dispatch = useDispatch();

  const removeBacktest = useCallback(
    (backtestId: number) => {
      dispatch(dataActions.removeStateBacktest(backtestId));
    },
    [dispatch],
  );

  const upsertBacktest = useCallback(
    (backtest: TDT.ApiBacktest) => {
      dispatch(dataActions.upsertBacktest(backtest));
    },
    [dispatch],
  );

  const setBacktests = useCallback(
    (newBacktests: TDT.ApiBacktest[]) => {
      dispatch(dataActions.setBacktests(newBacktests));
    },
    [dispatch],
  );

  const setBacktestFromExternal = useCallback(
    (data: CommonSettingsData & { config: string }) => {
      dispatch(dataActions.setBacktestFromExternal(data));
    },
    [dispatch],
  );

  const clearBacktestFromExternal = useCallback(() => {
    dispatch(dataActions.clearBacktestFromExternal());
  }, [dispatch]);

  return {
    removeBacktest,
    upsertBacktest,
    setBacktests,
    setBacktestFromExternal,
    clearBacktestFromExternal,
  };
};

/**
 * Hook for backtests data + actions (subscribes to backtests array)
 * Use this when you need to read the backtests list
 */
export const useBacktests = () => {
  const backtests = useDataState((state) => state.backtests);
  const backtestFromExternal = useDataState((state) => state.backtestFromExternal);
  const actions = useBacktestActions();

  return {
    backtests,
    backtestFromExternal,
    ...actions,
  };
};
